import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as api from '../lib/api';
import { useAuthStore } from '../store/auth';
import { Mark } from './brand/Logo';

/** Human "resets in 2d 3h" from an ISO timestamp. */
function untilLabel(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'soon';
  const h = Math.floor(ms / 3_600_000);
  const days = Math.floor(h / 24);
  return days >= 1 ? `${days}d ${h % 24}h` : `${h}h`;
}

/**
 * Always-visible strip telling the visitor they're in a demo sandbox on fake
 * data (Feature 24). Shows the reset control, a countdown to expiry, and a
 * conversion CTA. Renders nothing for real customer orgs.
 */
export function SandboxBanner() {
  const user = useAuthStore((s) => s.user);
  const [resetting, setResetting] = useState(false);

  if (user?.organizationKind !== 'SANDBOX') return null;

  async function reset() {
    if (resetting) return;
    setResetting(true);
    try {
      await api.resetSandbox();
      // Reload so every view refetches the freshly re-seeded data.
      window.location.reload();
    } catch {
      setResetting(false);
    }
  }

  return (
    <div
      className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-iris/40 px-4 py-1.5 text-center text-xs"
      style={{ backgroundColor: 'color-mix(in srgb, var(--iris) 14%, var(--obsidian))' }}
      role="status"
    >
      <span className="inline-flex items-center gap-1.5 font-medium text-bone">
        <Mark size={14} className="shrink-0" />
        You’re in a <strong className="text-iris">live demo</strong> — sample data, nothing
        connected.
      </span>
      {user.sandboxExpiresAt && (
        <span className="text-haze">Resets in {untilLabel(user.sandboxExpiresAt)}</span>
      )}
      <span className="flex items-center gap-2">
        <button
          type="button"
          onClick={reset}
          disabled={resetting}
          className="rounded-md px-2 py-0.5 text-haze underline-offset-2 transition-colors hover:text-bone hover:underline disabled:opacity-50"
        >
          {resetting ? 'Resetting…' : 'Reset demo'}
        </button>
        <Link
          to="/register"
          className="inline-flex h-6 items-center rounded-md bg-iris px-2.5 text-xs font-medium text-on-iris transition-colors hover:bg-iris-strong"
        >
          Get your own →
        </Link>
      </span>
    </div>
  );
}
